/**
 * ShipmentRateForm — Ocean cargo rate-search form.
 *
 * Summary of the UI/logic added on top of the original form (payload kept
 * backward compatible — `mainValue` in onSubmit is the source of truth):
 *
 *  • UI rebuilt with MUI + Tailwind + ShipmentRateForm.css (cards, bands, bars).
 *  • ORIGIN/DESTINATION DOOR-CY toggle drives originType/destinationType
 *    (CY => "PORT", DOOR => "DOOR"); switching clears the other side's stale
 *    value and collapses any multi-selection when leaving CY->CY.
 *  • CY->CY (both PORT) => port field is multi-select (max 8) with chips + N/8
 *    counter; ids are stored in the origin/destinationShipment strings joined
 *    by "&&" so the SAME state field works for single and multi, and the
 *    payload key comes out as "PortA&&PortB".
 *  • A DOOR side shows TWO stacked fields: Door (max 3, mirrors to
 *    originLocation) + CY ports (max 10, UI-only — not sent yet).
 *  • Goods Type "Cargo Type" == commodity (feeds `commonditytype`); Stuffing
 *    lives under the "Value Added Services" label (feeds `stuffing`); Search
 *    Currency is a searchable Autocomplete fed by api/general/currency (value
 *    = _id, label = currency_code).
 *  • Service Provider PLATFORM/ONLINE are multi-selects fed by
 *    api/general/platform & /online (ProviderSelect); the Ocean/Land header
 *    checkboxes bulk select/deselect all providers flagged for that mode.
 *  • Equipment modal opens as a centered, blurred MUI Dialog (Esc disabled).
 *  • Required-field validation on "Search Now" (origin, destination,
 *    equipment, provider, cargo type, cutoff date) shown inline; API errors
 *    surfaced via extractApiError().
 *  • New payload keys only: currency, Noofbl, platform[], online[].
 */
import React, {
  Suspense,
  lazy,
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { ErrorMessage, Form, Formik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Backdrop, CircularProgress, Dialog } from "@mui/material";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TimelineIcon from "@mui/icons-material/Timeline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import DirectionsBoatOutlinedIcon from "@mui/icons-material/DirectionsBoatOutlined";
import FlightOutlinedIcon from "@mui/icons-material/FlightOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { message, Modal, Skeleton, Tooltip } from "antd";
import TextError from "../../TextError";
import { useDispatch, useSelector } from "react-redux";
import {
  clearstate,
  getallshipments,
  getCurrencyList,
  getPlatformList,
  getOnlineList,
  getAllowedShippingLines,
  searchOceanRate,
  searchrates,
  searchratestate,
  setOceanEditQuery,
} from "../../../store/SearchRates/SearchRatesSlice";
import { useNavigate } from "react-router-dom";
import inactivefcl from "../../../assets/images/inactive-fcl-icon.svg";
import inactivelcl from "../../../assets/images/inactive-lcl-icon.svg";
import activefcl from "../../../assets/images/active-fcl-icon.svg";
import activelcl from "../../../assets/images/active-lcl-icon.svg";
import { IncoTerms } from "../../../utils/incoTermsDatas";
import {
  containerLineWeight,
  normalizePayloadContainer,
  toModalRow,
} from "../../../utils/oceanContainerWeight";
import OriginShipmentNew from "./OriginShipment/OriginShipmentNew";
import DestinationShipmentNew from "./DestinationShipment/DestinationShipmentNew";
import NewGoodsDetailsCom from "./ContainerDetails/NewGoodsDetailsCom";
import ProviderSelect from "./ProviderSelect";
import { isQueryLimitReached } from "../../../utils/queryLimit";
import VerifyEmailModal from "../../../components/common/VerifyEmailModal";
import "./ShipmentRateForm.css";

// Enables dayjs(str, "DD-MM-YYYY") parsing used by the cutoff-date field.
dayjs.extend(customParseFormat);

const OriginShipment = lazy(() => import("./OriginShipment/OriginShipment"));
const DestinationShipment = lazy(() =>
  import("./DestinationShipment/DestinationShipment")
);
const ContainerDetailsFCL = lazy(() =>
  import("./ContainerDetails/ContainerDetailsFCL")
);
const ContainerDetailsLCL = lazy(() =>
  import("./ContainerDetails/ContainerDetailsLCL")
);

const AMBER = "#FFAF00";
const AMBER_HOVER = "#E09800";

// Pull a human-readable message out of an error response, preferring the
// specific validation errors (e.g. "40DV is unsupported") over generic text.
const extractApiError = (res) => {
  const data = res?.payload?.data;
  const ve = data?.error?.validationErrors;
  if (ve && typeof ve === "object") {
    const msgs = Object.values(ve).flat().filter(Boolean);
    if (msgs.length) return msgs.join(", ");
  }
  return data?.error?.message || data?.message || "Something went wrong";
};

// Origin/Destination shipments are stored as a "&&"-joined string so a single
// state field works for both single (DOOR / mixed) and multi (CY->CY) modes.
const PORT_DELIM = "&&";
const splitPorts = (s) => (s ? String(s).split(PORT_DELIM).filter(Boolean) : []);
const firstPort = (s) => splitPorts(s)[0] || "";
const portCount = (s) => splitPorts(s).length;
const MAX_PORTS = 8;

const container_type = [
  { value: 1, label: "20 DV" },
  { value: 2, label: "20 RF" },
  { value: 3, label: "20 FR" },
  { value: 4, label: "40 DV" },
  { value: 5, label: "40 HC" },
  { value: 6, label: "40 RF" },
  { value: 7, label: "40 FR" },
  { value: 8, label: "20 OT" },
  { value: 9, label: "40 OT" },
  { value: 10, label: "45 DV" },
  { value: 11, label: "45 HC" },
  { value: 12, label: "45 RF" },
  { value: 13, label: "45 OT" },
  { value: 14, label: "20 GP" },
  { value: 15, label: "20 ISO" },
  { value: 16, label: "40 ISO" },
  { value: 17, label: "20 NOR" },
  { value: 18, label: "40 NOR" },
  { value: 19, label: "20 HC" },
  { value: 20, label: "40 GP" },
];

/* ---- Cargo Type == commodity type (feeds `commonditytype` in the payload) ---- */
const cargoTypeOptions =   
[ { value: "FAK", label: "Freight All Kinds (FAK)" },
  { value: "HAZ", label: "Hazardous Cargo" },
  { value: "NAC", label: "Non-Hazardous Cargo" },
  { value: "CHL", label: "Chilled Cargo" },
  { value: "FLX", label: "Flexibags" },
  { value: "FDS", label: "Foodstuff" },
  { value: "FRZ", label: "Frozen Cargo" },
  { value: "OTH", label: "Others" },
  { value: "OOG", label: "Out of Gauge (OOG)" },
  { value: "VEH", label: "Vehicles" },]

//  [
//   { value: "FAK", label: "FAK (Freight All Kinds)" },
//   { value: "HAZ", label: "HAZ (Hazardous Materials)" },
//   { value: "PER", label: "PER (Perishable Goods)" },
// ];
// Stuffing options (moved out of the Equipment modal). Rendered under the
// "Value Added Services" heading per request; feeds `stuffing` in the payload.
const stuffingOptions = 
[  { value: "Dock Stuffing", label: "Dock Stuffing" },
  {
    value: "Export Customs Brokerage",
    label: "Export Customs Brokerage",
  },
  {
    value: "Import Customs Brokerage",
    label: "Import Customs Brokerage",
  },]
// [
//   { value: "Factory/Dock", label: "Factory/Dock" },
//   { value: "Warehouse", label: "Warehouse" },
//   { value: "CFS", label: "CFS (Container Freight Station)" },
// ];
// Load-type glossary shown in the "Equipment Types" info tooltip.
const LOAD_TYPE_GLOSSARY = [
  { code: "FR", aka: "Flat Rack, Special Container" },
  {
    code: "GP",
    aka: "General Purpose, DV(Dry Van), FT(Feet), H0, CLI, Standard dry",
  },
  { code: "HC", aka: "High Cube, DV(Dry Van), FT(Feet), H0, CLO" },
  { code: "HR", aka: "High Reefer" },
  { code: "NR", aka: "Non Operating Reefer Container" },
  { code: "OT", aka: "Open Top, Special Container" },
  { code: "OTHR", aka: "Flexi Bags" },
  { code: "RE", aka: "Reefer" },
  { code: "S", aka: "Standard" },
  { code: "TK", aka: "Tank" },
];

const EquipmentInfo = () => (
  <div style={{ fontSize: 11, lineHeight: 1.4 }}>
    <table style={{ borderCollapse: "collapse", width: "100%" }}>
      <thead>
        <tr>
          <th
            style={{
              textAlign: "left",
              fontWeight: 700,
              padding: "0 14px 4px 0",
              borderBottom: "1px solid rgba(255,255,255,0.25)",
            }}
          >
            Load Type
          </th>
          <th
            style={{
              textAlign: "left",
              fontWeight: 700,
              padding: "0 0 4px",
              borderBottom: "1px solid rgba(255,255,255,0.25)",
            }}
          >
            Also known as
          </th>
        </tr>
      </thead>
      <tbody>
        {LOAD_TYPE_GLOSSARY.map((r) => (
          <tr key={r.code}>
            <td
              style={{
                fontWeight: 700,
                padding: "3px 14px 3px 0",
                verticalAlign: "top",
                whiteSpace: "nowrap",
              }}
            >
              {r.code}
            </td>
            <td style={{ padding: "3px 0" }}>{r.aka}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ marginTop: 6, opacity: 0.85 }}>
      Note: Select multiple load types to view rates
    </div>
  </div>
);

/* ---- Weight-capacity utilization (presentational / dummy) ---- */
const MAX_PAYLOAD_KG = {
  "20GP": 28180,
  "40GP": 26680,
  "40HC": 26512,
  "20RF": 27700,
  "40RF": 27900,
  "20OT": 28180,
  "40OT": 26680,
  "20DV": 28180,
  "40DV": 26680,
  "45HC": 27700,
  "45DV": 26680,
  "20HC": 28180,
};
// A saved query's boolean fields (originLocalCharges/destinationLocalCharges)
// can round-trip through the API as the STRING "false" rather than a real
// boolean — `!!"false"` is true, which would silently flip the toggle back
// on. Coerce explicitly instead of relying on truthiness.
const toBool = (v, fallback) =>
  v == null ? fallback : v === false || v === "false" || v === 0 || v === "0" ? false : !!v;
const normType = (t) => (t || "").toString().replace(/\s+/g, "").toUpperCase();
const utilClass = (pct) => (pct > 100 ? "over" : pct >= 85 ? "warn" : "");
function computeUtilization(containers) {
  let totalWeight = 0;
  let totalCapacity = 0;
  const rows = (containers || []).map((c) => {
    const qty = Number(c?.quantity) || 0;
    const cap = MAX_PAYLOAD_KG[normType(c?.type)] || 26680;
    const capRow = cap * qty;
    // kgs is the line total (legacy rows without weightType are per container).
    const weightRow = qty > 0 ? containerLineWeight(c) : 0;
    totalCapacity += capRow;
    totalWeight += weightRow;
    const pct = capRow > 0 ? Math.round((weightRow / capRow) * 100) : 0;
    return { label: `${c?.type} ×${qty}`, pct, cls: utilClass(pct) };
  });
  const overall =
    totalCapacity > 0 ? Math.round((totalWeight / totalCapacity) * 100) : 0;
  return { rows, overall };
}

const smallSelectSx = {
  "& .MuiInputBase-root": { fontSize: 13, background: "#fff" },
};
const amberSwitchSx = {
  width: 32,
  height: 18,
  padding: 0,
  display: "flex",
  flexShrink: 0,
  "& .MuiSwitch-switchBase": {
    padding: "2px",
    "&.Mui-checked": {
      transform: "translateX(14px)",
      color: "#fff",
      "& + .MuiSwitch-track": { backgroundColor: AMBER, opacity: 1 },
    },
  },
  "& .MuiSwitch-thumb": { width: 14, height: 14, boxShadow: "none" },
  "& .MuiSwitch-track": {
    borderRadius: 10,
    backgroundColor: "#c7ccd4",
    opacity: 1,
  },
};
const amberCheckSx = {
  p: 0.4,
  color: "#c7ccd4",
  "&.Mui-checked": { color: AMBER },
};
const toggleGroupSx = {
  "& .MuiToggleButton-root": {
    textTransform: "none",
    fontSize: 10,
    fontWeight: 600,
    py: 0.2,
    px: 0.9,
    lineHeight: 1,
    color: "#8b93a1",
    border: "1px solid #d8dce3",
  },
  "& .MuiToggleButton-root.Mui-selected": {
    backgroundColor: `${AMBER} !important`,
    color: "#fff !important",
  },
  "& .MuiToggleButton-root.Mui-selected:hover": {
    backgroundColor: `${AMBER} !important`,
  },
};

// `type` is only ever set by the KAM admin views in src/pages/Admin/Queries.jsx
// ("kam_admin" for a read-only view, "kam_admin_edit" while editing a pending
// query) — the customer-facing /search-rates page never passes it, so this
// stays fully editable there. "kam_admin" locks the whole form (view/prefill
// only); "kam_admin_edit" leaves every field editable but still hides the
// form's own submit button, since the KAM edit screen drives "Search Rate"
// via the exposed ref instead.
const ShipmentRateForm = forwardRef(({ searchBy, searchFormData, type }, ref) => {
  const isReadOnly = type === "kam_admin";
  const hideSubmitButton = type === "kam_admin" || type === "kam_admin_edit";
  // "kam_admin_edit" is used for a KAM re-quoting a customer's already-submitted
  // query: only the Cutoff Date may change here, everything else about the
  // original query (itinerary, goods type, equipment, charges/terms) stays
  // locked to what the customer submitted.
  const lockNonCutoffFields = type === "kam_admin_edit";
  const kamEditLockStyle = {
    pointerEvents: "none",
    opacity: 0.6,
    userSelect: "none",
  };
  const formikRef = useRef(null);
  useImperativeHandle(ref, () => ({
    submitForm: () => formikRef.current?.submitForm(),
  }));
  const [containerLoad, setContainerLoad] = useState(
    searchFormData?.searchquery?.containerLoad
      ? searchFormData?.searchquery?.containerLoad
      : "FCL"
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [formikData, setFormikData] = useState(null);
  const [popForm, setPopForm] = useState();
  const [shipPorts, setShipPorts] = useState();
  const [webopen, setWebOpen] = useState(true);
  // Prefill origin/destination selections from a previous query (Edit Query).
  // These local states drive the actual submit, so they must be seeded here.
  const editQuery = searchFormData?.searchquery;
  // Update / re-quote case: the query was deep-linked from single_booking_data
  // (NewSearchRates stamps `fromSingleBooking`). The route is fixed for an
  // existing booking, so the ORIGIN/DESTINATION shipment fields and the
  // CY/DOOR toggle are locked (non-editable) in this case.
  const isUpdateCase = !!editQuery?.fromSingleBooking;
  // Some callers (e.g. the KAM admin Queries list) hand over a raw query
  // record from an older schema where the port/location fields live under
  // `originPort`/`destinationPort` (mirroring the Air form's
  // `originAirport`/`destinationAirport`) instead of this form's own
  // `originShipment`/`destinationShipment` shape — only the "identifier"
  // field's name differs (originPort/destinationPort vs
  // originShipment/destinationShipment); location/pickup/marker/country are
  // shared. Fall back to that shape so Edit Query still auto-fills.
  const originQuery =
    editQuery?.originShipment ||
    (editQuery?.originPort && {
      originShipment: editQuery.originPort.originPort,
      originLocation: editQuery.originPort.originLocation,
      originPickup: editQuery.originPort.originPickup,
      originLocationMarker: editQuery.originPort.originLocationMarker,
      originPortMarker: editQuery.originPort.originPortMarker,
      originLocationCountry: editQuery.originPort.originLocationCountry,
    });
  const destinationQuery =
    editQuery?.destinationShipment ||
    (editQuery?.destinationPort && {
      destinationShipment: editQuery.destinationPort.destinationPort,
      destinationLocation: editQuery.destinationPort.destinationLocation,
      destinationPickup: editQuery.destinationPort.destinationPickup,
      destinationLocationMarker: editQuery.destinationPort.destinationLocationMarker,
      destinationPortMarker: editQuery.destinationPort.destinationPortMarker,
      destinationLocationCountry: editQuery.destinationPort.destinationLocationCountry,
    });
  const [originType, setOriginType] = useState(
    editQuery?.originType || "PORT"
  );
  const [originShipment, setOriginShipment] = useState(
    originQuery?.originShipment || ""
  );
  const [originlocation, setOriginLocation] = useState(
    originQuery?.originLocation || ""
  );

  const [destinationType, setDestinationType] = useState(
    editQuery?.destinationType || "PORT"
  );
  const [destinationShipment, setDestinationShipment] = useState(
    destinationQuery?.destinationShipment || ""
  );
  const [destinationlocation, setDestinationLocation] = useState(
    destinationQuery?.destinationLocation || ""
  );
  // const [openBackdrop, setOpenBackdrop] = useState(false);
  // True after a failed "Search Now" so required-field errors become visible.
  const [attempted, setAttempted] = useState(false);
  // Currency options fetched from api/general/currency ({ value:_id, label:code })
  const [currencyOptions, setCurrencyOptions] = useState([]);

  // Multi-port selection (with chips + N/8 counter) is only allowed when BOTH
  // ends are CY (PORT). Any DOOR involvement keeps them single-select.
  const isCyToCy = originType === "PORT" && destinationType === "PORT";
  // Default the currency to USD's _id when available, else the first option.
  const defaultCurrencyId =
    currencyOptions.find((o) => o.label === "USD")?.value ||
    currencyOptions[0]?.value ||
    "";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const allShipmentState = useSelector(searchratestate);

  // Unverified-user search gate (utils/queryLimit), shared with air: the
  // backend counts air and ocean searches together, so 3 searches of any mix
  // use up the free ones. A blocked search is held here and run once the
  // email is verified.
  const userDetails = useSelector((s) => s.data?.data?.data?.user);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const pendingSearchRef = useRef(null);
  // Guards the async limit check against a double-click firing two searches.
  const limitCheckRef = useRef(false);

  // Provider _ids flagged for a given mode ("ocean" | "land") within a list.
  const idsByMode = (listKey, mode) =>
    (allShipmentState?.[listKey] || [])
      .filter((i) => i && i._id && Number(i[mode]) === 1)
      .map((i) => i._id);

  // Whether every provider of a mode is currently selected. Drives the header
  // checkbox off the actual selection so it reflects prefilled (Edit Query)
  // providers once the provider lists have loaded — not a separate boolean.
  const isModeAllSelected = (formik, mode, listKey, fieldKey) => {
    const ids = idsByMode(listKey, mode);
    if (!ids.length) return false;
    const sel = formik.values[fieldKey] || [];
    return ids.every((id) => sel.includes(id));
  };

  // Ocean / Land header checkbox = "select all providers of that mode".
  // Unchecking removes that mode's ids, but keeps any still required by the
  // other mode when it's also fully selected (handles ocean+land providers).
  const handleModeToggle = (
    formik,
    checked,
    mode,
    listKey,
    fieldKey,
    oceanKey,
    landKey
  ) => {
    formik.setFieldValue(mode === "ocean" ? oceanKey : landKey, checked);
    const current = formik.values[fieldKey] || [];
    const modeIds = idsByMode(listKey, mode);
    if (checked) {
      formik.setFieldValue(
        fieldKey,
        Array.from(new Set([...current, ...modeIds]))
      );
    } else {
      const otherMode = mode === "ocean" ? "land" : "ocean";
      const otherChecked = isModeAllSelected(formik, otherMode, listKey, fieldKey);
      const keep = otherChecked
        ? new Set(idsByMode(listKey, otherMode))
        : new Set();
      const modeSet = new Set(modeIds);
      formik.setFieldValue(
        fieldKey,
        current.filter((id) => !modeSet.has(id) || keep.has(id))
      );
    }
  };

  const handleClick = (event, formik, popform) => {
    console.log(
      formik,
      formik.values.containerDetails?.packagingDetails?.totalWeight
    );
    console.log(formik.values.containerDetails?.incoTerms);
    console.log(popform);
    setAnchorEl(event.currentTarget);
    setFormikData(formik);
    setPopForm(popform);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  // Reset the whole form: Formik values + the state that lives outside Formik
  // (port selections, DOOR/CY types, container load) + cached search results.
  const handleReset = (formik) => {
    formik.resetForm();
    setAttempted(false);
    setContainerLoad(
      searchFormData?.searchquery?.containerLoad
        ? searchFormData?.searchquery?.containerLoad
        : "FCL"
    );
    setOriginType("PORT");
    setOriginShipment("");
    setOriginLocation("");
    setDestinationType("PORT");
    setDestinationShipment("");
    setDestinationLocation("");
    dispatch(clearstate());
  };
  async function findflights(values, { skipLimitCheck = false } = {}) {
    // Unverified customers past their free searches stay on this page with
    // the verify-email modal instead of going to the results page.
    if (!skipLimitCheck) {
      if (limitCheckRef.current) return;
      limitCheckRef.current = true;
      let blocked = false;
      try {
        blocked = await isQueryLimitReached(userDetails);
      } finally {
        limitCheckRef.current = false;
      }
      if (blocked) {
        pendingSearchRef.current = values;
        setShowVerifyModal(true);
        return;
      }
    }
    // Redirect on Search CLICK: navigate immediately and let the results page
    // read the search result from redux (searchOceanRate reducers) once it
    // arrives. `state.data` = the submitted query (query bar + Edit prefill).
    //
    // The backend persists every ocean search as a query and returns it on the
    // response as `data.savedQuery` (its `_id` is the query_id). Once the search
    // resolves, fold that queryId back into the form's redux prefill
    // (`data.data.nodeData.searchquery`) so a SUBSEQUENT search re-quotes the
    // SAME saved query — onSubmit sends `queryId` + `requestType: 2` (see the
    // `editQuery?.queryId` branch) — instead of spawning a brand-new query.
    dispatch(searchOceanRate(values))
      .then((res) => {
        // The thunk swallows failures and resolves with `err.response` (or
        // undefined), so guard on the HTTP status and the presence of a saved
        // query before persisting anything.
        if (res?.payload?.status !== 200) return;
        const savedQueryId = res?.payload?.data?.savedQuery?._id;
        if (!savedQueryId) return;
        // `values` (mainValue) already matches the prefill shape the form reads
        // (originShipment/destinationShipment objects, goodsDetails, etc.); just
        // stamp the returned queryId onto it.
        const searchquery = { ...values, queryId: savedQueryId };
        const nodeData = { searchquery };
        dispatch(setOceanEditQuery({ data: { nodeData }, nodeData }));
      })
      .catch(() => {
        // Defensive only — searchOceanRate never rejects; a persisted queryId is
        // a nice-to-have, so an unexpected throw must not break the search.
      });
    navigate("/search-rates/ocean-rates-result-new", {
      state: {
        data: values,
        commondity: values?.commonditytype || "",
        stuffing: values?.stuffing || "",
      },
    });
  }
  function openmodal(values, msg, type) {
    Modal.confirm({
      title: msg,
      okText: "Proceed",
      cancelButtonProps: {
        style: {
          color: "#FFAF00",
          borderColor: "#FFAF00",
        },
      },
      onOk() {
        if (type === "origin") {
          values.originShipment.originPickup = false;
          values.originShipment.originLocation = "";
          values.originShipment.originLocationMarker = {};
          values.originShipment.originPortMarker = {};
          values.originShipment.originLocationCountry = "";
        } else if (type === "destination") {
          values.destinationShipment.destinationPickup = false;
          values.destinationShipment.destinationLocation = "";
          values.destinationShipment.destinationLocationMarker = {};
          values.destinationShipment.destinationPortMarker = {};
          values.destinationShipment.destinationLocationCountry = "";
        } else if (type === "thirdcountry") {
          values.originShipment.originPickup = false;
          values.originShipment.originLocation = "";
          values.destinationShipment.destinationPickup = false;
          values.destinationShipment.destinationLocation = "";
        }
        console.log(values);
        findflights(values);
      },
      onCancel() {},
      okButtonProps: {
        style: {
          color: "#ffffff",
          backgroundColor: "#FCB614",
        },
      },
    });
  }
  // Required-field validation for the main form. Reads origin/destination from
  // component state and everything else from Formik values. Returns an errors
  // map ({} when valid) so the same logic drives both blocking and display.
  const validateForm = (values) => {
    const errors = {};
    if (!originShipment) errors.origin = "Origin is required";
    if (!destinationShipment) errors.destination = "Destination is required";

    const containers = Array.isArray(values?.containerDetails)
      ? values.containerDetails
      : [];
    const hasEquipment =
      containers.length > 0 &&
      containers.every((c) => c?.type && c?.quantity && c?.kgs);
    if (!hasEquipment) errors.equipment = "Add equipment details";

    const hasProvider =
      (values?.platformProviders?.length || 0) > 0 ||
      (values?.onlineProviders?.length || 0) > 0;
    if (!hasProvider) errors.provider = "Select at least one provider";

    if (!values?.cargoTypeSelection) errors.cargoType = "Cargo type is required";
    if (!values?.goodReadyDate) errors.cutoffDate = "Cutoff date is required";
    return errors;
  };

  const onSubmit = (values, setprops) => {
    console.log("values", values);

    const errors = validateForm(values);
    if (Object.keys(errors).length > 0) {
      setAttempted(true);
      message.error("Please fill all the required fields");
      return;
    }

    const mainValue = {
      cargoType: "Ocean Cargo",
      containerLoad: "FCL",
      originType: originType || "",
      serviceModeOrigin: originType === "DOOR" ? "DOOR" : "CY",
      originLocalCharges: !!values?.originLocalCharges,
      originShipment: {
        originShipment: originShipment || "",
        originLocation: originlocation || "",
        originPickup: false,
        originLocationMarker: {},
        originPortMarker: {},
        originLocationCountry: "",
      },
      destinationType: destinationType || "",
      serviceModeDestination: destinationType === "DOOR" ? "DOOR" : "CY",
      destinationLocalCharges: !!values?.destinationLocalCharges,
      destinationShipment: {
        destinationShipment: destinationShipment || "",
        destinationLocation: destinationlocation || "",
        destinationPickup: false,
        destinationLocationMarker: {},
        destinationPortMarker: {},
        destinationLocationCountry: "",
      },
      goodsDetails: {
        cargoreadydate: values?.goodReadyDate || "",
      },
      paymentTerms: {
        paymentType: "",
        incoTerms: values?.incoterms || "",
      },
      commonditytype: values?.cargoTypeSelection || "",
      stuffing: values?.stuffing || "",
      // Normalised so every row goes out as { type, quantity, kgs (line
      // total), weightunit: "KG", weightType } — including rows prefilled
      // from an older query the user never re-opened the modal for.
      containerDetails: (values?.containerDetails || []).map(
        normalizePayloadContainer
      ),
      // Selected currency _id (from api/general/currency)
      currency: values?.searchCurrency || defaultCurrencyId || "",
      // No. of B/L entered in the Equipment modal (Other Details tab). The
      // modal stores it as a string, so compare numerically — a "0" seeded
      // from an older record is truthy and would be searched as zero.
      Noofbl:
        Number(values?.GoodDetails?.noOfBL) > 0
          ? Number(values.GoodDetails.noOfBL)
          : 1,
      // Selected provider _ids (from api/general/platform & api/general/online)
      platform: values?.platformProviders || [],
      online: values?.onlineProviders || [],
    };

    // Every search carries a requestType: 1 = fresh new-booking search, 2 =
    // re-quote of a query that's ALREADY BOOKED (confirmBooking's
    // isUpdateFlow reads this straight off the search response to decide
    // create-ocean-booking vs. update-ocean-booking). `editQuery?.queryId`
    // alone isn't a safe proxy for that — it's just as truthy for a KAM
    // re-searching a not-yet-booked query via Edit Query (Auto Quoted /
    // Quote Pending), which was flipping requestType to 2 and making a
    // brand-new booking call update-ocean-booking on a query with nothing to
    // update. Only bump to 2 when the query's own status says it's already
    // booked — checking both `Quote_status` (KAM query-list record shape)
    // and `booking_status` (single_booking_data booking-record shape), since
    // editQuery can be either depending on how this form was opened.
    const editQueryAlreadyBooked = [editQuery?.Quote_status, editQuery?.booking_status].some(
      (s) => String(s || "").toLowerCase().trim().replace(/\s+/g, "") === "bookingconfirmed"
    );
    mainValue.requestType = 1;
    if (editQuery?.queryId) {
      mainValue.queryId = editQuery.queryId;
      if (editQueryAlreadyBooked) {
        if (editQuery?.fromSingleBooking) {
        mainValue.requestType = 2;
      }
      }
    }

    // A KAM searching from the Query Detail edit screen (the only place this
    // form is submitted as "kam_admin_edit" — see Queries.jsx) is quoting on
    // behalf of the customer who owns this query, not themselves — the
    // backend requires customerId to know whose account the query/booking
    // belongs to. `userId` is the query record's own owner field; userDetails
    // is a populated join of the same user (see adminController.js's
    // get_quotes_list projection), used here only as a fallback.
    if (type === "kam_admin_edit") {
      mainValue.customerId = editQuery?.userId || editQuery?.userDetails?._id || "";
    }

    console.log("mainValue", mainValue);

    // NOTE: The legacy incoterm/VAS-driven validation branches have been
    // retired; the payload shape below is unchanged and drives the search.
    // Navigation to the results page happens INSIDE findflights, only after
    // the search resolves — so we land on the listing once the data has
    // actually arrived (no premature redirect to an empty results page).
    findflights(mainValue);
  };
  let ContainerInit = [],
    PackageInt = [];
  if (containerLoad === "FCL") {
    if (
      searchFormData?.searchquery?.containerDetails?.containerDetails?.length > 0
    ) {
      searchFormData?.searchquery?.containerDetails?.containerDetails?.forEach(
        (item) => {
          console.log(item);
          ContainerInit.push({
            containerSize: item.containerSize,
            containerType: item.containerType,
            containerSubType: item.containerSubType,
            cargoWeight: item.cargoWeight,
            containerCount: item.containerCount,
          });
        }
      );
    } else {
      ContainerInit.push({
        containerSize: "20",
        containerType: "Standard Dry",
        containerSubType: "General",
        cargoWeight: "18",
        containerCount: "1",
      });
    }
  } else {
    if (
      searchFormData?.searchquery?.containerDetails?.packagingDetails
        ?.packagingDatas?.length > 0
    ) {
      searchFormData?.searchquery?.containerDetails?.packagingDetails?.packagingDatas?.forEach(
        (item) => {
          PackageInt.push({
            noofPackages: item.noofPackages,
            length: item.length,
            width: item.width,
            height: item.height,
            unit: item.unit,
            weight: item.weight,
            totalsubweight: item.totalsubweight,
            totalsubvolume: item.totalsubvolume,
            totalsubpackages: item.totalsubpackages,
          });
        }
      );
    } else {
      PackageInt.push({
        noofPackages: "1",
        length: "100",
        width: "100",
        height: "100",
        unit: "CM",
        weight: "1",
        totalsubweight: "",
        totalsubvolume: "",
        totalsubpackages: "",
      });
    }
  }

  // A previous query's `containerDetails` can come back in either shape:
  // a flat `[{ type, quantity, kgs }]` array (this form's own search
  // payload — see onSubmit's mainValue.containerDetails) or nested under a
  // `{ containerDetails: [...], packagingDetails, vaslist, ... }` object
  // (the raw saved query record the KAM Queries table/detail fetch hands
  // over — confirmed against a real search_cargo_result_detail response).
  // Only checking the flat shape left the Equipment modal blank whenever a
  // KAM opened Edit Query on that raw-record shape.
  const editQueryContainerDetails = Array.isArray(editQuery?.containerDetails)
    ? editQuery.containerDetails
    : editQuery?.containerDetails?.containerDetails || [];

  const initialValues = {
    cargoType: searchBy,
    containerLoad: containerLoad,
    originType: "",
    originShipment: {
      originShipment: originQuery?.originShipment || "",
      originLocation: originQuery?.originLocation || "",
      originPickup: originQuery?.originPickup === "1",
      originLocationMarker: originQuery?.originLocationMarker || {},
      originPortMarker: originQuery?.originPortMarker || {},
      originLocationCountry: originQuery?.originLocationCountry || "",
    },
    destinationType: "",
    destinationShipment: {
      destinationShipment: destinationQuery?.destinationShipment || "",
      destinationLocation: destinationQuery?.destinationLocation || "",
      destinationPickup: destinationQuery?.destinationPickup === "1",
      destinationLocationMarker:
        destinationQuery?.destinationLocationMarker || {},
      destinationPortMarker: destinationQuery?.destinationPortMarker || {},
      destinationLocationCountry:
        destinationQuery?.destinationLocationCountry || "",
    },
    // Container / cargo details — prefilled from a previous query (Edit Query)
    // when present, otherwise empty so the Equipment modal opens with a blank row.
    GoodDetails: {
      // Seed the Equipment modal's rows too (it reads GoodDetails.containers,
      // while the summary/utilisation reads containerDetails).
      // The payload's kgs is the line total; the modal wants the typed value
      // back (KG/UNIT rows are divided by quantity again).
      containers: editQueryContainerDetails.map(toModalRow),
      // Every ocean shipment has at least one master B/L, so an unset (or
      // blank, on an older query record) count seeds as 1 rather than empty —
      // the Equipment modal's field and the search payload both read this.
      // Compared numerically because the count is stored as a string: "0"
      // is truthy and would seed the modal on zero.
      noOfBL: Number(editQuery?.Noofbl) > 0 ? Number(editQuery.Noofbl) : 1,
    },
    // Both of these are written at the TOP level by this form's own onSubmit
    // (mainValue.commonditytype / mainValue.stuffing), but the raw query
    // record the KAM Queries view hands over instead nests them under
    // goodsDetails (goodsDetails.commodity_description / goodsDetails.stuffing
    // — confirmed against a real search_cargo_result_detail response). Reading
    // only the top-level field silently dropped both back to their defaults
    // ("FAK" / empty "Value Added Services") on that view.
    commonditytype: editQuery?.commonditytype || editQuery?.goodsDetails?.commodity_description || "",
    stuffing: editQuery?.stuffing || editQuery?.goodsDetails?.stuffing || "",
    containerDetails: editQueryContainerDetails,
    goodReadyDate: editQuery?.goodsDetails?.cargoreadydate || "",
    incoterms: editQuery?.paymentTerms?.incoTerms || "",
    /* ---- New mockup columns: prefilled from previous query when editing ---- */
    originMode: editQuery?.originType === "DOOR" ? "DOOR" : "CY",
    destinationMode: editQuery?.destinationType === "DOOR" ? "DOOR" : "CY",
    // Persist the previous query's Local Charges toggle when editing/
    // re-quoting instead of always resetting it back to on.
    originLocalCharges: toBool(editQuery?.originLocalCharges, true),
    destinationLocalCharges: toBool(editQuery?.destinationLocalCharges, true),
    cargoTypeSelection: editQuery?.commonditytype || editQuery?.goodsDetails?.commodity_description || "",
    platformProviders: Array.isArray(editQuery?.platform)
      ? editQuery.platform
      : [],
    platformOcean: false,
    platformLand: false,
    onlineProviders: Array.isArray(editQuery?.online) ? editQuery.online : [],
    onlineOcean: false,
    onlineLand: false,
    searchCurrency: editQuery?.currency || "",
  };
  const validationSchema = Yup.object().shape({
    originShipment: Yup.object().shape({
      originShipment: Yup.string().required("Origin Seaport is required"),
    }),
    destinationShipment: Yup.object().shape({
      destinationShipment: Yup.string().required(
        "Destination Seaport is required"
      ),
    }),
  });

  const getContainerLabel = (value) => {
    const ct = container_type?.find((c) => c.value === Number(value));
    return ct ? ct.label : "";
  };

  useEffect(() => {
    if (allShipmentState?.allshipports?.length > 0) {
      let optionData = [];
      allShipmentState?.allshipports?.map((item) => {
        return optionData.push({
          value: `${item.port_name} - (${item.port_code})`,
          label: `${item.port_name} - (${item.port_code})`,
        });
      });
      setShipPorts(optionData);
    } else {
      dispatch(getallshipments());
    }
  }, [dispatch, allShipmentState]);
  // Fetch currency / platform / online lists once on mount (if not loaded).
  useEffect(() => {
    if (!(allShipmentState?.currencyList?.length > 0)) {
      dispatch(getCurrencyList());
    }
    if (!(allShipmentState?.platformList?.length > 0)) {
      dispatch(getPlatformList()).then((res) => {
        if (res?.payload?.status !== 200) {
          message.error("Unable to load platform list");
        }
      });
    }
    if (!(allShipmentState?.onlineList?.length > 0)) {
      dispatch(getOnlineList()).then((res) => {
        if (res?.payload?.status !== 200) {
          message.error("Unable to load online list");
        }
      });
    }
    // Stored in redux (allowedShippingLines); not shown on the form yet.
    dispatch(getAllowedShippingLines());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
  // Map the currency list -> options ({ value: _id, label: currency_code }).
  useEffect(() => {
    const cl = allShipmentState?.currencyList;
    if (Array.isArray(cl) && cl.length > 0) {
      setCurrencyOptions(
        cl
          .filter((i) => i && i._id)
          .map((i) => ({ value: i._id, label: i.currency_code }))
      );
    }
  }, [allShipmentState]);
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 1024) {
        setWebOpen(false);
      } else {
        setWebOpen(true);
      }
    }
    window.addEventListener("resize", handleResize);
    window.addEventListener("load", handleResize);
    handleResize();
  });

  console.log("formikDatashipmentrate", formikData);

  return (
    <>
      <Formik
        initialValues={initialValues}
        // validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {(formik) => {
          formikRef.current = formik;
          const appliedContainers = Array.isArray(formik.values.containerDetails)
            ? formik.values.containerDetails
            : Array.isArray(formik.values?.GoodDetails?.containers)
            ? formik.values.GoodDetails.containers
            : [];
          const util = computeUtilization(appliedContainers);
          const eqSummary = appliedContainers.length
            ? appliedContainers.map((c) => `${c.type} ×${c.quantity}`).join(", ")
            : "Add container details";

          // Live errors, shown only after a failed submit; clear as fields fill.
          const errors = attempted ? validateForm(formik.values) : {};

          return (
            <Form autoComplete="off">
              <div
                className="sr-wrap"
                style={
                  isReadOnly
                    ? { pointerEvents: "none", opacity: 0.75 }
                    : undefined
                }
              >
                {/* ============ TOP BAR ============ */}
                {/* <div className="sr-card sr-topbar">
                  <div className="sr-topbar-left">
                    <span className="sr-brand-icon">
                      <Inventory2OutlinedIcon sx={{ fontSize: 19 }} />
                    </span>
                    <div className="sr-topbar-title">
                      <h1>Book Your Cargo</h1>
                      <p>Select a mode and search for rates</p>
                    </div>
                  </div>
                  <div className="sr-mode-tabs">
                    <Button
                      type="button"
                      startIcon={<FlightOutlinedIcon sx={{ fontSize: 15 }} />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#4b5563",
                        border: "1px solid #d8dce3",
                        borderRadius: "8px",
                        px: 1.8,
                      }}
                    >
                      Air Cargo
                    </Button>
                    <Button
                      type="button"
                      startIcon={
                        <DirectionsBoatOutlinedIcon sx={{ fontSize: 15 }} />
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#fff",
                        borderRadius: "8px",
                        px: 1.8,
                        background: `linear-gradient(135deg, ${AMBER}, ${AMBER_HOVER})`,
                        "&:hover": {
                          background: `linear-gradient(135deg, ${AMBER}, ${AMBER_HOVER})`,
                        },
                      }}
                    >
                      Ocean Cargo
                    </Button>
                    <Button
                      type="button"
                      startIcon={
                        <WorkOutlineOutlinedIcon sx={{ fontSize: 15 }} />
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#4b5563",
                        border: "1px solid #d8dce3",
                        borderRadius: "8px",
                        px: 1.8,
                      }}
                    >
                      Courier
                    </Button>
                  </div>
                </div> */}

                {/* ============ FCL toggle (functional) ============ */}
                <div
                  className="mb-3"
                  style={lockNonCutoffFields ? kamEditLockStyle : undefined}
                >
                  <button
                    onClick={() => {
                      setContainerLoad("FCL");
                      dispatch(clearstate());
                    }}
                    type="button"
                    className={`bg-white font-medium rounded-lg text-sm px-4 py-2 text-center inline-flex items-center justify-center gap-2 border ${
                      containerLoad === "FCL"
                        ? "text-primary border-primary"
                        : "text-containerload border-containerload"
                    }`}
                  >
                    <img
                      src={containerLoad === "FCL" ? activefcl : inactivefcl}
                      alt="FCL"
                    />
                    <span>Full Container Load (FCL)</span>
                  </button>
                </div>

                {/* ============ TOP ROW: Itinerary + Goods & Carrier ============ */}
                <div className="sr-top-row">
                  {/* ---- Itinerary ---- */}
                  <div className="sr-card">
                    <div className="sr-heading">
                      <span className="sr-icon-box">
                        <TimelineIcon sx={{ fontSize: 13 }} />
                      </span>
                      <h2>Itinerary</h2>
                    </div>

                    <div
                      className="sr-od-grid"
                      style={lockNonCutoffFields ? kamEditLockStyle : undefined}
                    >
                      {/* ORIGIN */}
                      <div className="sr-od-panel">
                        <div className="sr-od-panel-header">
                          <div className="sr-od-head-left flex items-center gap-1.5">
                            <span className="sr-od-name">
                              ORIGIN <span className="req">*</span>
                            </span>
                            <ToggleButtonGroup
                              exclusive
                              size="small"
                              disabled={isUpdateCase}
                              value={originType === "DOOR" ? "DOOR" : "CY"}
                              onChange={(e, v) => {
                                if (!v) return; // ignore de-select (always keep one)
                                formik.setFieldValue("originMode", v);
                                if (v === "DOOR") {
                                  setOriginType("DOOR");
                                  // switching to DOOR clears any picked port
                                  setOriginShipment("");
                                  // no longer CY->CY: destination can't be multi
                                  setDestinationShipment((prev) =>
                                    firstPort(prev)
                                  );
                                } else {
                                  // CY maps to PORT; clear any typed door location
                                  setOriginType("PORT");
                                  setOriginLocation("");
                                }
                              }}
                              sx={toggleGroupSx}
                            >
                              <ToggleButton value="DOOR">DOOR</ToggleButton>
                              <ToggleButton value="CY">CY</ToggleButton>
                            </ToggleButtonGroup>
                          </div>
                          <FormControlLabel
                            sx={{ m: 0, flexShrink: 0 }}
                            control={
                              <Switch
                                size="small"
                                checked={!!formik.values.originLocalCharges}
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    "originLocalCharges",
                                    e.target.checked
                                  )
                                }
                                sx={amberSwitchSx}
                              />
                            }
                            label={
                              <span
                                style={{
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  color: "#4b5563",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Local Charges
                              </span>
                            }
                            labelPlacement="start"
                          />
                        </div>
                        <div className="sr-od-panel-body">
                          {/* Locked in the update case — the booked route's
                              origin can't be changed while re-quoting. */}
                          <div
                            style={
                              isUpdateCase
                                ? {
                                    pointerEvents: "none",
                                    opacity: 0.6,
                                    userSelect: "none",
                                  }
                                : undefined
                            }
                          >
                            <OriginShipmentNew
                              formikData={formikData}
                              setAnchorEl={setAnchorEl}
                              shipPorts={shipPorts}
                              allshipports={allShipmentState?.allshipports}
                              originType={originType}
                              setOriginType={setOriginType}
                              originShipment={originShipment}
                              setOriginShipment={setOriginShipment}
                              originlocation={originlocation}
                              setOriginLocation={setOriginLocation}
                              multiple={isCyToCy}
                              maxCount={MAX_PORTS}
                              doorToDoor={originType === "DOOR"}
                            />
                          </div>

                          <div className="flex items-center justify-between mt-1.5">
                            <span className="sr-error">
                              {errors.origin || ""}
                            </span>
                            {/* Count is a port concept — shown only in CY mode
                                (in DOOR the child renders its own N/3, N/10). */}
                            {originType === "PORT" && (
                              <span className="sr-loc-count">
                                {isCyToCy
                                  ? `${portCount(originShipment)} / ${MAX_PORTS}`
                                  : "1 / 8"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* SWAP */}
                      <div className="sr-swap">
                        {/* <span className="circle">
                          <SwapHorizIcon sx={{ fontSize: 16 }} />
                        </span> */}
                      </div>

                      {/* DESTINATION */}
                      <div className="sr-od-panel">
                        <div className="sr-od-panel-header">
                          <div className="sr-od-head-left flex items-center gap-1.5">
                            <span className="sr-od-name">
                              DESTINATION <span className="req">*</span>
                            </span>
                            <ToggleButtonGroup
                              exclusive
                              size="small"
                              disabled={isUpdateCase}
                              value={destinationType === "DOOR" ? "DOOR" : "CY"}
                              onChange={(e, v) => {
                                if (!v) return; // ignore de-select (always keep one)
                                formik.setFieldValue("destinationMode", v);
                                if (v === "DOOR") {
                                  setDestinationType("DOOR");
                                  // switching to DOOR clears any picked port
                                  setDestinationShipment("");
                                  // no longer CY->CY: origin can't be multi
                                  setOriginShipment((prev) => firstPort(prev));
                                } else {
                                  // CY maps to PORT; clear any typed door location
                                  setDestinationType("PORT");
                                  setDestinationLocation("");
                                }
                              }}
                              sx={toggleGroupSx}
                            >
                              <ToggleButton value="CY">CY</ToggleButton>
                              <ToggleButton value="DOOR">DOOR</ToggleButton>
                            </ToggleButtonGroup>
                          </div>
                          <FormControlLabel
                            sx={{ m: 0, flexShrink: 0 }}
                            control={
                              <Switch
                                size="small"
                                checked={!!formik.values.destinationLocalCharges}
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    "destinationLocalCharges",
                                    e.target.checked
                                  )
                                }
                                sx={amberSwitchSx}
                              />
                            }
                            label={
                              <span
                                style={{
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  color: "#4b5563",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Local Charges
                              </span>
                            }
                            labelPlacement="start"
                          />
                        </div>
                        <div className="sr-od-panel-body">
                          {/* Locked in the update case — the booked route's
                              destination can't be changed while re-quoting. */}
                          <div
                            style={
                              isUpdateCase
                                ? {
                                    pointerEvents: "none",
                                    opacity: 0.6,
                                    userSelect: "none",
                                  }
                                : undefined
                            }
                          >
                            <DestinationShipmentNew
                              formikData={formikData}
                              setAnchorEl={setAnchorEl}
                              shipPorts={shipPorts}
                              allshipports={allShipmentState?.allshipports}
                              destinationType={destinationType}
                              setDestinationType={setDestinationType}
                              destinationShipment={destinationShipment}
                              setDestinationShipment={setDestinationShipment}
                              destinationlocation={destinationlocation}
                              setDestinationLocation={setDestinationLocation}
                              multiple={isCyToCy}
                              maxCount={MAX_PORTS}
                              doorToDoor={destinationType === "DOOR"}
                            />
                          </div>

                          <div className="flex items-center justify-between mt-1.5">
                            <span className="sr-error">
                              {errors.destination || ""}
                            </span>
                            {/* Count is a port concept — shown only in CY mode
                                (in DOOR the child renders its own N/3, N/10). */}
                            {destinationType === "PORT" && (
                              <span className="sr-loc-count">
                                {isCyToCy
                                  ? `${portCount(destinationShipment)} / ${MAX_PORTS}`
                                  : "1 / 8"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Good Ready Date */}
                    <div className="mt-3" style={{ maxWidth: 260 }}>
                      <label className="sr-label">
                       Cutoff Date <span className="req">*</span>
                      </label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          format="DD-MM-YYYY"
                          // Restrict selection to today .. 3 months from today.
                          minDate={dayjs()}
                          maxDate={dayjs().add(3, "month")}
                          value={
                            formik.values.goodReadyDate
                              ? dayjs(
                                  formik.values.goodReadyDate,
                                  "DD-MM-YYYY"
                                )
                              : null
                          }
                          onChange={(date) => {
                            const formatted =
                              date && dayjs(date).isValid()
                                ? dayjs(date).format("DD-MM-YYYY")
                                : "";
                            formik.setFieldValue("goodReadyDate", formatted);
                          }}
                          slotProps={{
                            textField: {
                              id: "goodReadyDate",
                              name: "goodReadyDate",
                              size: "small",
                              fullWidth: true,
                              error: !!errors.cutoffDate,
                              sx: smallSelectSx,
                            },
                          }}
                        />
                      </LocalizationProvider>
                      {errors.cutoffDate && (
                        <span className="sr-error">{errors.cutoffDate}</span>
                      )}
                    </div>
                  </div>

                  {/* ---- Goods & Carrier ---- */}
                  <div
                    className="sr-card"
                    style={lockNonCutoffFields ? kamEditLockStyle : undefined}
                  >
                    <div className="sr-heading">
                      <span className="sr-icon-box">
                        <Inventory2OutlinedIcon sx={{ fontSize: 13 }} />
                      </span>
                      <h2>Goods Type</h2>
                    </div>

                    <div>
                      <label className="sr-label">
                        Cargo Type <span className="req">*</span>
                      </label>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={formik.values.cargoTypeSelection ?? ""}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "cargoTypeSelection",
                            e.target.value
                          )
                        }
                        SelectProps={{ displayEmpty: true }}
                        sx={smallSelectSx}
                      >
                        <MenuItem value="" disabled>
                          <span style={{ color: "#9aa1ad" }}>
                            Select Cargo Type
                          </span>
                        </MenuItem>
                        {cargoTypeOptions.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      {errors.cargoType && (
                        <span className="sr-error">{errors.cargoType}</span>
                      )}
                    </div>

                    <div className="sr-subheading">
                      <span className="sr-icon-box sm">
                        <LocalShippingOutlinedIcon sx={{ fontSize: 11 }} />
                      </span>
                      <h3>Service Provider <span className="req">*</span></h3>
                    </div>

                    {/* Platform */}
                    <div className="sr-provider-block">
                      <div className="sr-provider-top">
                        <span className="sr-ptitle">
                          PLATFORM{" "}
                          <Tooltip
                            title="Rate ingested to & stored on the Freightify Platform e.g. from carrier rate sheets"
                            mouseEnterDelay={0}
                            mouseLeaveDelay={0}
                            overlayStyle={{ maxWidth: 340 }}
                            overlayInnerStyle={{
                              fontSize: 11,
                              lineHeight: 1.4,
                              whiteSpace: "normal",
                            }}
                          >
                            <span className="sr-info-dot">i</span>
                          </Tooltip>
                        </span>
                        <span className="flex gap-2">
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={isModeAllSelected(
                                  formik,
                                  "ocean",
                                  "platformList",
                                  "platformProviders"
                                )}
                                onChange={(e) =>
                                  handleModeToggle(
                                    formik,
                                    e.target.checked,
                                    "ocean",
                                    "platformList",
                                    "platformProviders",
                                    "platformOcean",
                                    "platformLand"
                                  )
                                }
                                sx={amberCheckSx}
                              />
                            }
                            label={
                              <span style={{ fontSize: 10.5, fontWeight: 600 }}>
                                Ocean
                              </span>
                            }
                          />
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={isModeAllSelected(
                                  formik,
                                  "land",
                                  "platformList",
                                  "platformProviders"
                                )}
                                onChange={(e) =>
                                  handleModeToggle(
                                    formik,
                                    e.target.checked,
                                    "land",
                                    "platformList",
                                    "platformProviders",
                                    "platformOcean",
                                    "platformLand"
                                  )
                                }
                                sx={amberCheckSx}
                              />
                            }
                            label={
                              <span style={{ fontSize: 10.5, fontWeight: 600 }}>
                                Land
                              </span>
                            }
                          />
                        </span>
                      </div>
                      <ProviderSelect
                        items={allShipmentState?.platformList}
                        value={formik.values.platformProviders || []}
                        onChange={(vals) =>
                          formik.setFieldValue("platformProviders", vals)
                        }
                        placeholder="Select Ocean/Land"
                      />
                    </div>

                    {/* Online */}
                    <div className="sr-provider-block" style={{ marginBottom: 0 }}>
                      <div className="sr-provider-top">
                        <span className="sr-ptitle">ONLINE</span>
                        <span className="flex gap-2">
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={isModeAllSelected(
                                  formik,
                                  "ocean",
                                  "onlineList",
                                  "onlineProviders"
                                )}
                                onChange={(e) =>
                                  handleModeToggle(
                                    formik,
                                    e.target.checked,
                                    "ocean",
                                    "onlineList",
                                    "onlineProviders",
                                    "onlineOcean",
                                    "onlineLand"
                                  )
                                }
                                sx={amberCheckSx}
                              />
                            }
                            label={
                              <span style={{ fontSize: 10.5, fontWeight: 600 }}>
                                Ocean
                              </span>
                            }
                          />
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={isModeAllSelected(
                                  formik,
                                  "land",
                                  "onlineList",
                                  "onlineProviders"
                                )}
                                onChange={(e) =>
                                  handleModeToggle(
                                    formik,
                                    e.target.checked,
                                    "land",
                                    "onlineList",
                                    "onlineProviders",
                                    "onlineOcean",
                                    "onlineLand"
                                  )
                                }
                                sx={amberCheckSx}
                              />
                            }
                            label={
                              <span style={{ fontSize: 10.5, fontWeight: 600 }}>
                                Land
                              </span>
                            }
                          />
                        </span>
                      </div>
                      <ProviderSelect
                        items={allShipmentState?.onlineList}
                        value={formik.values.onlineProviders || []}
                        onChange={(vals) =>
                          formik.setFieldValue("onlineProviders", vals)
                        }
                        placeholder="Select Ocean/Land"
                      />
                    </div>
                    {errors.provider && (
                      <span className="sr-error">{errors.provider}</span>
                    )}
                  </div>
                </div>

                {/* ============ EQUIPMENT ROW ============ */}
                <div
                  className="sr-card sr-tight"
                  style={lockNonCutoffFields ? kamEditLockStyle : undefined}
                >
                  <div className="sr-equip-band">
                    <span>Equipment Details</span>
                    <span className="sub">Freight equipment for this shipment</span>
                  </div>
                  <div className="sr-equip-grid">
                    <div>
                      <label className="sr-label">
                        Equipment Types <span className="req">*</span>{" "}
                        <Tooltip
                          title={<EquipmentInfo />}
                          mouseEnterDelay={0}
                          mouseLeaveDelay={0}
                          overlayStyle={{ maxWidth: 460 }}
                          overlayInnerStyle={{ fontSize: 11 }}
                        >
                          <span
                            className="sr-info-dot"
                            style={{
                              display: "inline-flex",
                              verticalAlign: "middle",
                            }}
                          >
                            i
                          </span>
                        </Tooltip>
                      </label>
                      {containerLoad === "FCL" && (
                        <Button
                          type="button"
                          fullWidth
                          onClick={(e) =>
                            handleClick(e, formik, "ContainerDetailsFCL")
                          }
                          endIcon={
                            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                          }
                          sx={{
                            justifyContent: "space-between",
                            textTransform: "none",
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#1f2430",
                            background: "#fff",
                            border: "1px solid #d8dce3",
                            borderRadius: "8px",
                            px: 1.4,
                            py: 1,
                            "&:hover": {
                              borderColor: AMBER,
                              background: "#fff",
                            },
                          }}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {eqSummary}
                          </span>
                        </Button>
                      )}
                      {errors.equipment && (
                        <span className="sr-error">{errors.equipment}</span>
                      )}
                    </div>

                    <div>
                      <div className="sr-util-title-row">
                        <span className="t">Weight Capacity Utilization</span>
                        <span className="overall">Overall: {util.overall}%</span>
                      </div>
                      {util.rows.length ? (
                        util.rows.map((r, i) => (
                          <div className="sr-util-row" key={i}>
                            <span className="sr-util-label">{r.label}</span>
                            <div className="sr-util-track">
                              <div
                                className={`sr-util-fill ${r.cls}`}
                                style={{ width: `${Math.min(r.pct, 100)}%` }}
                              />
                            </div>
                            <span className="sr-util-pct">{r.pct}%</span>
                          </div>
                        ))
                      ) : (
                        <div className="sr-util-empty">
                          Add equipment to see utilization.
                        </div>
                      )}
                      <div className="sr-util-note">
                        Based on standard max payload per ISO container type.
                        Actual limits vary by carrier and equipment.
                      </div>
                    </div>
                  </div>
                </div>

                {/* ============ CHARGES & TERMS ============ */}
                <div
                  className="sr-card"
                  style={lockNonCutoffFields ? kamEditLockStyle : undefined}
                >
                  <div className="sr-heading">
                    <span className="sr-icon-box">
                      <ReceiptLongOutlinedIcon sx={{ fontSize: 13 }} />
                    </span>
                    <h2>Charges &amp; Terms</h2>
                  </div>
                  <div className="sr-grid-3">
                    <div>
                      <label className="sr-label">Incoterm</label>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        id="incoterms"
                        name="incoterms"
                        value={formik.values.incoterms ?? ""}
                        onChange={(e) =>
                          formik.setFieldValue("incoterms", e.target.value)
                        }
                        SelectProps={{ displayEmpty: true }}
                        sx={smallSelectSx}
                      >
                        <MenuItem value="" disabled>
                          <span style={{ color: "#9aa1ad" }}>
                            Select Incoterm
                          </span>
                        </MenuItem>
   <MenuItem value="CIP">CIP - export</MenuItem>
                        <MenuItem value="CIF">CIF - export</MenuItem>
                        <MenuItem value="FAS">
                          FAS - import
                        </MenuItem>

                        {/* <MenuItem value="">Select Incoterm</MenuItem> */}
                        {/* <MenuItem value="EXW">EXW - Ex Works</MenuItem>
                        <MenuItem value="FOB">FOB - Free On Board</MenuItem>
                        <MenuItem value="CIF">
                          CIF - Cost Insurance and Freight
                        </MenuItem>
                        <MenuItem value="DAP">DAP - Delivered at Place</MenuItem> */}
                      </TextField>
                      <ErrorMessage name="incoterms" component={TextError} />
                    </div>

                    <div>
                      <label className="sr-label">Value Added Services</label>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={formik.values.stuffing || ""}
                        onChange={(e) =>
                          formik.setFieldValue("stuffing", e.target.value)
                        }
                        SelectProps={{ displayEmpty: true }}
                        sx={smallSelectSx}
                      >
                        <MenuItem value="" disabled>
                          <span style={{ color: "#9aa1ad" }}>Select VAS</span>
                        </MenuItem>
                        {stuffingOptions.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>

                    <div>
                      <label className="sr-label">Currency</label>
                      <Autocomplete
                        size="small"
                        fullWidth
                        disableClearable
                        options={currencyOptions}
                        getOptionLabel={(o) => o?.label || ""}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        value={
                          currencyOptions.find(
                            (o) => o.value === formik.values.searchCurrency
                          ) || null
                        }
                        onChange={(e, newVal) =>
                          formik.setFieldValue(
                            "searchCurrency",
                            newVal ? newVal.value : ""
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Currency"
                          />
                        )}
                        sx={smallSelectSx}
                      />
                    </div>
                  </div>
                </div>

                {/* ============ FOOTER ============ */}
                <div
                  className="sr-footer-row"
                  style={lockNonCutoffFields ? kamEditLockStyle : undefined}
                >
                  <button
                    type="button"
                    className="sr-reset-link"
                    onClick={() => handleReset(formik)}
                  >
                    <RestartAltIcon sx={{ fontSize: 15 }} />
                    Reset Search
                  </button>
                </div>

                {!hideSubmitButton && (
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: 14.5,
                      py: 1.4,
                      borderRadius: "10px",
                      background: `linear-gradient(135deg, ${AMBER}, ${AMBER_HOVER})`,
                      "&:hover": {
                        background: `linear-gradient(135deg, ${AMBER}, ${AMBER_HOVER})`,
                        filter: "brightness(1.03)",
                      },
                    }}
                  >
                    Search Now
                  </Button>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>

      {/* Equipment & Cargo Details modal — centered Dialog with a blurred
          backdrop (replaces the old anchored Popover). Esc is disabled so the
          modal only closes via Cancel/Apply/backdrop; fullScreen on mobile. */}
      <Dialog
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown
        fullWidth
        maxWidth="md"
        fullScreen={webopen === false}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(31,36,48,0.45)",
              backdropFilter: "blur(3px)",
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: webopen === false ? 0 : "16px",
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(31,36,48,0.28)",
          },
        }}
      >
        {popForm === "OriginShipment" ? (
          <Suspense fallback={<Skeleton active />}>
            {/* <OriginShipment
              formikData={formikData}
              setAnchorEl={setAnchorEl}
              shipPorts={shipPorts}
              allshipports={allShipmentState?.allshipports}
            /> */}
          </Suspense>
        ) : popForm === "DestinationShipment" ? (
          <Suspense fallback={<Skeleton active />}>
            <DestinationShipment
              formikData={formikData}
              setAnchorEl={setAnchorEl}
              shipPorts={shipPorts}
              allshipports={allShipmentState?.allshipports}
            />
          </Suspense>
        ) : popForm === "ContainerDetailsFCL" && containerLoad === "FCL" ? (
          <Suspense fallback={<Skeleton active />}>
            <NewGoodsDetailsCom
              formikData={formikData}
              setAnchorEl={setAnchorEl}
              allshipports={allShipmentState?.allshipports}
            />
          </Suspense>
        ) : (
          popForm === "ContainerDetailsLCL" &&
          containerLoad === "LCL" && (
            <Suspense fallback={<Skeleton active />}>
              <ContainerDetailsLCL
                formikData={formikData}
                setAnchorEl={setAnchorEl}
                allshipports={allShipmentState?.allshipports}
              />
            </Suspense>
          )
        )}
      </Dialog>

      {/* <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={openBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop> */}

      <VerifyEmailModal
        open={showVerifyModal}
        onClose={() => {
          pendingSearchRef.current = null;
          setShowVerifyModal(false);
        }}
        // Verified: run the search that was held back.
        onVerified={() => {
          setShowVerifyModal(false);
          const pending = pendingSearchRef.current;
          pendingSearchRef.current = null;
          if (pending) findflights(pending, { skipLimitCheck: true });
        }}
        defaultEmail={userDetails?.email || userDetails?.unverified_email}
        userId={userDetails?.id}
      />
    </>
  );
});

export default ShipmentRateForm;
